// protocol.js — DataChannel message schema validation for Peerly
// All peer-originating messages are validated here before being acted upon.
// Unknown or malformed messages are rejected. Fail closed, not open.

import { PROTOCOL, EMOJI_LIST } from './config.js';

/**
 * Validates a string field: must be a string within length bounds.
 */
function isValidString(val, maxLen) {
  return typeof val === 'string' && val.length > 0 && val.length <= maxLen;
}

/**
 * Checks if a value is a known emoji (single grapheme cluster from our list or standard emoji pattern).
 */
function isValidEmoji(val) {
  if (typeof val !== 'string' || val.length === 0 || val.length > 8) return false;
  if (EMOJI_LIST.includes(val)) return true;
  // Allow standard emoji sequences (basic check — emoji are 1-8 chars of emoji codepoints)
  // eslint-disable-next-line no-misleading-character-class
  const emojiPattern = /^[\p{Emoji}\u200d\ufe0f]+$/u;
  return emojiPattern.test(val);
}

/**
 * Per-type validators. Each returns { valid: boolean, error?: string }.
 * Only whitelisted fields are extracted; everything else is stripped.
 */
const VALIDATORS = {
  chat(data) {
    if (!isValidString(data.message, PROTOCOL.MAX_CHAT_LENGTH)) {
      return { valid: false, error: 'Invalid chat message' };
    }
    if (!isValidString(data.nickname, PROTOCOL.MAX_NICKNAME_LENGTH)) {
      return { valid: false, error: 'Invalid chat nickname' };
    }
    return {
      valid: true,
      sanitized: {
        type: 'chat',
        message: data.message.slice(0, PROTOCOL.MAX_CHAT_LENGTH),
        nickname: data.nickname.slice(0, PROTOCOL.MAX_NICKNAME_LENGTH),
      },
    };
  },

  command(data) {
    if (!isValidString(data.message, PROTOCOL.MAX_COMMAND_LENGTH)) {
      return { valid: false, error: 'Invalid command message' };
    }
    return {
      valid: true,
      sanitized: {
        type: 'command',
        message: data.message.slice(0, PROTOCOL.MAX_COMMAND_LENGTH),
      },
    };
  },

  emoji(data) {
    if (!isValidEmoji(data.emoji)) {
      return { valid: false, error: 'Invalid emoji' };
    }
    return {
      valid: true,
      sanitized: { type: 'emoji', emoji: data.emoji },
    };
  },

  nickname(data) {
    if (!isValidString(data.nickname, PROTOCOL.MAX_NICKNAME_LENGTH)) {
      return { valid: false, error: 'Invalid nickname' };
    }
    return {
      valid: true,
      sanitized: {
        type: 'nickname',
        nickname: data.nickname.slice(0, PROTOCOL.MAX_NICKNAME_LENGTH),
      },
    };
  },

  pyah(data) {
    if (!isValidEmoji(data.emoji)) {
      return { valid: false, error: 'Invalid pyah emoji' };
    }
    return {
      valid: true,
      sanitized: { type: 'pyah', emoji: data.emoji },
    };
  },

  celebration(data) {
    if (!isValidEmoji(data.emoji)) {
      return { valid: false, error: 'Invalid celebration emoji' };
    }
    return {
      valid: true,
      sanitized: { type: 'celebration', emoji: data.emoji },
    };
  },

  presence(data) {
    const allowed = ['online', 'away', 'leaving'];
    if (typeof data.status !== 'string' || !allowed.includes(data.status)) {
      return { valid: false, error: 'Invalid presence status' };
    }
    return {
      valid: true,
      sanitized: { type: 'presence', status: data.status },
    };
  },

  'file-start'(data) {
    if (!isValidString(data.name, PROTOCOL.MAX_FILE_NAME_LENGTH)) {
      return { valid: false, error: 'Invalid file name' };
    }
    if (typeof data.size !== 'number' || data.size <= 0 || data.size > PROTOCOL.MAX_FILE_SIZE) {
      return { valid: false, error: 'Invalid file size' };
    }
    if (typeof data.id !== 'string' || data.id.length > 64) {
      return { valid: false, error: 'Invalid file transfer id' };
    }
    return {
      valid: true,
      sanitized: { type: 'file-start', id: data.id, name: data.name, size: data.size },
    };
  },

  'file-chunk'(data) {
    if (typeof data.id !== 'string' || data.id.length > 64) {
      return { valid: false, error: 'Invalid file transfer id' };
    }
    if (typeof data.seq !== 'number' || data.seq < 0) {
      return { valid: false, error: 'Invalid chunk sequence number' };
    }
    // data.data is a base64 string — validate it's a string with reasonable length
    if (typeof data.data !== 'string' || data.data.length > PROTOCOL.FILE_CHUNK_SIZE * 2) {
      return { valid: false, error: 'Invalid chunk data' };
    }
    return {
      valid: true,
      sanitized: { type: 'file-chunk', id: data.id, seq: data.seq, data: data.data },
    };
  },

  'file-end'(data) {
    if (typeof data.id !== 'string' || data.id.length > 64) {
      return { valid: false, error: 'Invalid file transfer id' };
    }
    return {
      valid: true,
      sanitized: { type: 'file-end', id: data.id },
    };
  },

  'file-accept'(data) {
    if (typeof data.id !== 'string' || data.id.length > 64) {
      return { valid: false, error: 'Invalid file transfer id' };
    }
    return {
      valid: true,
      sanitized: { type: 'file-accept', id: data.id },
    };
  },

  'file-deny'(data) {
    if (typeof data.id !== 'string' || data.id.length > 64) {
      return { valid: false, error: 'Invalid file transfer id' };
    }
    return {
      valid: true,
      sanitized: { type: 'file-deny', id: data.id },
    };
  },
};

/**
 * Parse and validate an incoming DataChannel message.
 * Returns { valid: true, data: sanitizedPayload } or { valid: false, error: string }.
 *
 * This is the ONLY entry point for processing peer messages.
 * It rejects unknown types, oversized payloads, and malformed JSON.
 */
export function parseIncoming(raw) {
  // Guard: raw must be a string
  if (typeof raw !== 'string') {
    return { valid: false, error: 'Non-string payload' };
  }

  // Guard: reject excessively large payloads
  // file-chunk messages carry base64-encoded data and are validated by the
  // per-type validator against FILE_CHUNK_SIZE * 2, so they need a higher limit.
  const isLikelyFileChunk = raw.startsWith('{"type":"file-chunk"');
  const maxRawLen = isLikelyFileChunk ? PROTOCOL.FILE_CHUNK_SIZE * 4 : 10_000;
  if (raw.length > maxRawLen) {
    return { valid: false, error: 'Payload too large' };
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { valid: false, error: 'Invalid JSON' };
  }

  // Guard: must be a plain object
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { valid: false, error: 'Payload is not an object' };
  }

  // Guard: type must be a known string
  const { type } = parsed;
  if (typeof type !== 'string' || !PROTOCOL.ALLOWED_MESSAGE_TYPES.has(type)) {
    return { valid: false, error: `Unknown message type: ${String(type).slice(0, 30)}` };
  }

  // Delegate to per-type validator
  const validator = VALIDATORS[type];
  if (!validator) {
    return { valid: false, error: `No validator for type: ${type}` };
  }

  const result = validator(parsed);
  if (!result.valid) {
    return { valid: false, error: result.error };
  }

  return { valid: true, data: result.sanitized };
}

/**
 * Create an outgoing message. This is a convenience that ensures we only send well-formed data.
 */
export function createMessage(type, payload) {
  return JSON.stringify({ type, ...payload });
}
